import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from 'react-bootstrap';
import { AdminTableColumn } from '../../components/table/interfaces/AdminTableColumn';
import { AdminTableInputFilter } from '../../components/table/filters/AdminTableInputFilter';
import { createStringFilterFunction } from '../../components/table/functions/createStringFilterFunction';
import { EntityAlias } from '../dto/EntityAlias';

/** Sources a person chose, as opposed to ones the system generated from a name. */
const DELIBERATE = new Set(['curated', 'learned']);

export function useEntityAliasColumns(): AdminTableColumn<EntityAlias>[] {
  const { t } = useTranslation();

  return useMemo(
    () => [
      {
        title: t('Word').toString(),
        predicate: (item) => (
          <span className={item.retired_at ? 'text-muted text-decoration-line-through' : undefined}>{item.surface}</span>
        ),
        sortingPredicate: (item) => item.surface,
        filterConfig: {
          filterFn: createStringFilterFunction((item) => item.surface),
          component: AdminTableInputFilter,
        },
      },
      {
        title: t('Means').toString(),
        predicate: (item) => item.canonical,
        sortingPredicate: (item) => item.canonical,
        filterConfig: {
          filterFn: createStringFilterFunction((item) => item.canonical),
          component: AdminTableInputFilter,
        },
      },
      {
        title: t('Kind').toString(),
        predicate: (item) => <Badge bg="light" text="dark">{item.entity_type}</Badge>,
        sortingPredicate: (item) => item.entity_type,
      },
      {
        // Where a name came from decides how much weight to give it: one a person chose or
        // taught the bot is evidence; one derived from spelling a canonical name is not.
        title: t('Source').toString(),
        predicate: (item) => (
          <Badge bg={DELIBERATE.has(item.source) ? 'primary' : 'secondary'}>{item.source}</Badge>
        ),
        sortingPredicate: (item) => item.source,
      },
      {
        title: t('Times confirmed').toString(),
        predicate: (item) => item.confirmed_count,
        sortingPredicate: (item) => item.confirmed_count,
      },
      {
        title: t('Status').toString(),
        predicate: (item) =>
          item.retired_at ? <Badge bg="dark">{t('Retired')}</Badge> : <Badge bg="success">{t('Active')}</Badge>,
        sortingPredicate: (item) => (item.retired_at ? 1 : 0),
      },
    ],
    [t],
  );
}
